import React, { useState } from "react";
import { useNavigate, Navigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { LOGOUT_REASON_KEY } from "@/lib/api";
import { PAYOUT_ROLES } from "@/components/ProtectedRoute";
import { MessageCircle, Lock, User as UserIcon, Loader2 } from "lucide-react";

export default function Login() {
  const { user, login } = useAuth();
  const navigate = useNavigate();
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [logoutReason] = useState(() => sessionStorage.getItem(LOGOUT_REASON_KEY));

  const roleDest = (role) => {
    if (role === "resend_staff") return "/resend-only";
    if (role === "team_staff") return "/my-ledger";
    if (role === "ekyc_staff") return "/ekyc-desk";
    if (role === "delivery_man") return "/delivery";
    if (PAYOUT_ROLES.includes(role)) return "/my-payout";
    return "/home";
  };

  if (user && user !== false) {
    return <Navigate to={roleDest(user.role)} replace />;
  }

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    const res = await login(username.trim().toLowerCase(), password);
    setLoading(false);
    if (res.ok) {
      sessionStorage.removeItem(LOGOUT_REASON_KEY);
      navigate(roleDest(res.user?.role), { replace: true });
    } else {
      setError(res.error || "Login failed");
    }
  };

  return (
    <div
      className="min-h-screen w-full flex items-center justify-center p-4 relative overflow-hidden"
      style={{
        background:
          "radial-gradient(circle at 18% 22%, #DCE5DA 0%, transparent 45%), radial-gradient(circle at 82% 75%, #E8D8C5 0%, transparent 50%), radial-gradient(circle at 50% 50%, #F0EFE8 0%, #E8E6DD 100%)",
      }}
    >
      {/* Subtle grain overlay */}
      <div
        className="absolute inset-0 opacity-[0.35] pointer-events-none mix-blend-overlay"
        style={{
          backgroundImage:
            "url(\"data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='160' height='160' viewBox='0 0 160 160'><filter id='n'><feTurbulence type='fractalNoise' baseFrequency='0.85' numOctaves='2' stitchTiles='stitch'/><feColorMatrix values='0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0.55 0'/></filter><rect width='100%' height='100%' filter='url(%23n)'/></svg>\")",
        }}
      />

      {/* Decorative blobs */}
      <div
        className="absolute -top-40 -left-40 w-[420px] h-[420px] rounded-full opacity-30 blur-3xl pointer-events-none"
        style={{ background: "#385C4B" }}
      />
      <div
        className="absolute -bottom-40 -right-40 w-[480px] h-[480px] rounded-full opacity-25 blur-3xl pointer-events-none"
        style={{ background: "#C8835A" }}
      />
      <div className="w-full max-w-md relative z-10">
        <div className="bg-white/85 backdrop-blur-xl border border-white/60 shadow-[0_20px_60px_-15px_rgba(56,92,75,0.25)] rounded-2xl p-8">
          <div className="flex items-center gap-3 mb-8">
            <div className="w-11 h-11 rounded-lg bg-[#385C4B] flex items-center justify-center">
              <MessageCircle className="w-5 h-5 text-[#F5F5F0]" strokeWidth={1.75} />
            </div>
            <div>
              <div className="text-[10px] tracking-[0.25em] uppercase text-[#6B6B61] font-mono">
                Non Stop Chatting
              </div>
              <div className="text-lg font-semibold text-[#1C1C19] tracking-tight" style={{ fontFamily: "Outfit, sans-serif" }}>
                XYZ Chatting
              </div>
            </div>
          </div>

          <h1
            className="text-3xl font-semibold tracking-tight text-[#1C1C19] leading-none mb-2"
            style={{ fontFamily: "Outfit, sans-serif" }}
          >
            Sign in to continue
          </h1>
          <p className="text-sm text-[#6B6B61] mb-8" style={{ fontFamily: "Work Sans, sans-serif" }}>
            Use the credentials provided by your admin.
          </p>

          <form onSubmit={handleSubmit} className="space-y-5" data-testid="login-form">
            <div>
              <label className="text-xs tracking-[0.2em] uppercase text-[#6B6B61] font-mono mb-2 block">
                Username
              </label>
              <div className="flex items-center border border-[#DCDCD3] rounded-md bg-white px-3 focus-within:ring-2 focus-within:ring-[#385C4B] focus-within:border-[#385C4B] transition-colors">
                <UserIcon className="w-4 h-4 text-[#6B6B61]" strokeWidth={1.5} />
                <input
                  type="text"
                  autoComplete="username"
                  value={username}
                  onChange={(e) => setUsername(e.target.value)}
                  placeholder="your.username"
                  required
                  data-testid="login-username-input"
                  className="flex-1 bg-transparent outline-none px-3 py-3 text-[#1C1C19] placeholder:text-[#A7A79D]"
                  style={{ fontFamily: "Work Sans, sans-serif" }}
                />
              </div>
            </div>

            <div>
              <label className="text-xs tracking-[0.2em] uppercase text-[#6B6B61] font-mono mb-2 block">
                Password
              </label>
              <div className="flex items-center border border-[#DCDCD3] rounded-md bg-white px-3 focus-within:ring-2 focus-within:ring-[#385C4B] focus-within:border-[#385C4B] transition-colors">
                <Lock className="w-4 h-4 text-[#6B6B61]" strokeWidth={1.5} />
                <input
                  type="password"
                  autoComplete="current-password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="••••••••"
                  required
                  data-testid="login-password-input"
                  className="flex-1 bg-transparent outline-none px-3 py-3 text-[#1C1C19] placeholder:text-[#A7A79D]"
                  style={{ fontFamily: "Work Sans, sans-serif" }}
                />
              </div>
            </div>

            {logoutReason && !error && (
              <div
                className="border border-[#C8553D]/40 bg-[#C8553D]/10 text-[#C8553D] text-sm rounded-md px-3 py-2"
                data-testid="login-logout-reason"
              >
                {logoutReason}
              </div>
            )}

            {error && (
              <div
                className="border border-[#8A6046]/40 bg-[#8A6046]/10 text-[#8A6046] text-sm rounded-md px-3 py-2"
                data-testid="login-error"
              >
                {error}
              </div>
            )}

            <button
              type="submit"
              disabled={loading}
              data-testid="login-submit-button"
              className="w-full bg-[#385C4B] hover:bg-[#2D4A3C] text-[#F5F5F0] font-medium py-3 rounded-md transition-colors flex items-center justify-center gap-2 disabled:opacity-60"
              style={{ fontFamily: "Outfit, sans-serif" }}
            >
              {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : null}
              {loading ? "Signing in…" : "Sign In"}
            </button>
          </form>

          <div className="mt-8 pt-6 border-t border-[#DCDCD3]">
            <div className="text-[10px] tracking-[0.2em] uppercase text-[#6B6B61] font-mono">
              Need access?
            </div>
            <div className="text-sm text-[#1C1C19] mt-1" style={{ fontFamily: "Work Sans, sans-serif" }}>
              Contact your administrator for a new account.
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
